import React, { useState, useEffect } from 'react';
import './App.css';
import { supabase } from './supabase';
import { devLog } from './utils/logger';
import Login from './Login';
import Dashboard from './Dashboard';
import MarineOfferEnhanced from './MarineOfferEnhanced';
import PowerPlantBuilderIntegration from './PowerPlantBuilderIntegration';
import FinanceTracker from './FinanceTracker';

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentApp, setCurrentApp] = useState('dashboard');

  useEffect(() => {
    // Check if we have a token in the URL hash (OAuth callback)
    if (window.location.hash && window.location.hash.includes('access_token')) {
      devLog('OAuth token detected in URL');
      // Supabase will handle the token automatically
      // Clear the hash from the URL after a short delay to ensure Supabase processes it
      setTimeout(() => {
        window.history.replaceState(null, '', window.location.pathname);
      }, 100);
    }
    
    // Check if user is already logged in
    checkUser();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        devLog('Auth state changed:', event);
        if (event === 'SIGNED_IN' && session) {
          // Check email domain for access control
          const email = session.user.email;
          const allowedDomains = ['@fin-marine.com', '@atlantismarinepower.com', '@depamarine.com'];
          const isAllowed = allowedDomains.some(domain => email?.endsWith(domain));
          
          if (!isAllowed) {
            await supabase.auth.signOut();
            alert('Access denied. Only @fin-marine.com and @atlantismarinepower.com emails are allowed.');
            setUser(null);
            setCurrentApp('dashboard');
            // Clear any hash from URL
            window.history.replaceState(null, '', window.location.pathname);
            return;
          }
          
          // Set user and clear URL hash
          setUser(session.user);
          window.history.replaceState(null, '', window.location.pathname);
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setCurrentApp('dashboard');
          setLoading(false);
        } else if (event === 'USER_UPDATED') {
          setUser(session?.user || null);
          setLoading(false);
        } else {
          // For any other event, update user state
          setUser(session?.user || null);
          if (!loading && session?.user) {
            setLoading(false);
          }
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      devLog('CheckUser - authenticated:', Boolean(user));
      setUser(user);
    } catch (error) {
      console.error('CheckUser error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setCurrentApp('dashboard');
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  // Not logged in - show login page
  if (!user) {
    return <Login onLogin={setUser} />;
  }

  // Logged in - show appropriate screen
  return (
    <div className="App">
      {/* Navigation bar */}
      <div className="app-nav" style={{
        background: '#333',
        color: 'white',
        padding: '10px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div className="app-nav-group" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <h3 className="app-nav-title" style={{ margin: 0, cursor: 'pointer' }} onClick={() => setCurrentApp('dashboard')}>
            Atlantis Business Tools
          </h3>
          {currentApp !== 'dashboard' && (
            <button
              onClick={() => setCurrentApp('dashboard')}
              style={{
                background: 'transparent',
                border: '1px solid white',
                color: 'white',
                padding: '5px 15px',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              ← Back to Dashboard
            </button>
          )}
        </div>
        <div className="app-nav-group" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <span className="app-nav-email">{user.email}</span>
          <button
            onClick={handleLogout}
            style={{
              background: '#ff4444',
              border: 'none',
              color: 'white',
              padding: '5px 15px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      {currentApp === 'dashboard' && (
        <Dashboard user={user} onSelectApp={setCurrentApp} />
      )}
      
      {currentApp === 'marine' && (
        <MarineOfferEnhanced />
      )}
      
      {currentApp === 'power' && (
        <PowerPlantBuilderIntegration />
      )}

      {currentApp === 'finance' && (
        <FinanceTracker />
      )}
    </div>
  );
}

export default App;
// Deployment fix
